import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData, reputationItems } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    setFormStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9rh12vnahb7382ovme0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const resText = await res.text();
      let parsed;
      try { parsed = JSON.parse(resText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || resText;
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          setFormMessage('感谢您的留言，我们会尽快回复！');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 琴韵阁',
          description: '琴韵阁是国内领先的六角琴学习与选购平台，致力于传承六角琴艺，弘扬国乐之美。',
          url: 'https://www.liujiaoqin.com/about',
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <Breadcrumb />

        {/* Hero */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              {aboutData.tagline}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed max-w-2xl mx-auto">
              {aboutData.intro}
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/70">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500">{stat.value}</div>
                  <div className="mt-2 text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story & Values */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">我们的故事</h2>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">{aboutData.story}</p>
              </div>
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">核心价值观</h2>
                <div className="space-y-4">
                  {aboutData.values.map((val) => (
                    <div key={val.title} className="flex gap-3">
                      <div className="w-10 h-10 flex-shrink-0 rounded-md bg-accent-100 flex items-center justify-center">
                        <i className={`${val.icon} text-accent-600 w-5 h-5 flex items-center justify-center`}></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900">{val.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1">{val.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">行业口碑</h2>
              <p className="mt-2 text-sm text-foreground-500">值得信赖的专业平台</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {reputationItems.map((item) => (
                <div key={item.title} className="bg-background-100 rounded-lg p-5 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-accent-600 text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">用户评价</h2>
              <p className="mt-2 text-sm text-foreground-500">来自真实用户的反馈</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {aboutData.testimonials.map((t) => (
                <div key={t.name} className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-4">{t.content}</p>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                    <div className="text-xs text-foreground-400">{t.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">联系我们</h2>
              <p className="mt-2 text-sm text-foreground-500">有任何问题或建议，欢迎留言</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="space-y-4 text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-400">电子邮箱</div>
                    <div className="text-foreground-800 font-medium">{aboutData.contact.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-400">客服电话</div>
                    <div className="text-foreground-800 font-medium">{aboutData.contact.phone}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-400">公司地址</div>
                    <div className="text-foreground-800 font-medium">{aboutData.contact.address}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-400">微信公众号</div>
                    <div className="text-foreground-800 font-medium">{aboutData.contact.wechat}</div>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="space-y-4 bg-background-100 rounded-lg p-5 border border-background-200/70"
                data-readdy-form=""
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2 text-sm border border-background-300/60 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3 py-2 text-sm border border-background-300/60 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-3 py-2 text-sm border border-background-300/60 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                </div>
                <input
                  name="phone_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-hp"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full bg-primary-500 text-background-50 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'loading' ? '提交中...' : '提交留言'}
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600 text-center">{formMessage}</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">{formMessage}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}